import React, { useEffect, useState } from 'react';
import {
    Table,
    Button,
    Space,
    Modal,
    Form,
    Input,
    Rate,
    Switch,
    Select,
    message,
    Typography,
    Card,
    Popconfirm,
    Tooltip
} from "antd";
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    MailOutlined,
    GoogleOutlined,
    FacebookOutlined,
    InstagramOutlined,
    TwitterOutlined,
    StarOutlined,
    GlobalOutlined,
    CommentOutlined,
} from "@ant-design/icons";
import apiClient from '../../utils/constants/apiClient';
import { Container } from "../Users/styles";

const { Title } = Typography;
const { TextArea } = Input;

const PLATFORM_OPTIONS = [
    { value: 'google', label: 'Google Reviews', icon: <GoogleOutlined /> },
    { value: 'facebook', label: 'Facebook', icon: <FacebookOutlined /> },
    { value: 'instagram', label: 'Instagram', icon: <InstagramOutlined /> },
    { value: 'twitter', label: 'Twitter / X', icon: <TwitterOutlined /> },
    { value: 'email', label: 'Email', icon: <MailOutlined /> },
    { value: 'gmail', label: 'Gmail', icon: <GoogleOutlined style={{ color: '#EA4335' }} /> },
    { value: 'outlook', label: 'Outlook', icon: <MailOutlined style={{ color: '#0078D4' }} /> },
    { value: 'trustpilot', label: 'Trustpilot', icon: <StarOutlined style={{ color: '#00B67A' }} /> },
    { value: 'yelp', label: 'Yelp', icon: <CommentOutlined style={{ color: '#FF1A1A' }} /> },
    { value: 'tripadvisor', label: 'TripAdvisor', icon: <GlobalOutlined style={{ color: '#34E0A1' }} /> },
];

const getPlatformIcon = (platform: string | null) => {
    const found = PLATFORM_OPTIONS.find(p => p.value === platform);
    return found ? found.icon : null;
};

interface Review {
    id: string;
    title: string;
    rate: number;
    review: string;
    user_name: string;
    date: string;
    is_active: boolean;
    show_on_homepage: boolean;
    platform: string | null;
    createdAt: string;
    updatedAt: string;
}

const Reviews: React.FC = () => {
    const [reviews, setReviews] = useState<Review[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingReview, setEditingReview] = useState<Review | null>(null);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchReviews();
    }, []);

    const fetchReviews = async () => {
        try {
            setLoading(true);
            const response = await apiClient.get('/reviews?all=true');
            setReviews(response.data);
        } catch (error) {
            message.error('Failed to fetch reviews');
            console.error('Error fetching reviews:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setEditingReview(null);
        form.resetFields();
        form.setFieldsValue({
            rate: 5,
            is_active: true,
            show_on_homepage: false,
            date: new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })
        });
        setModalVisible(true);
    };

    const handleEdit = (record: Review) => {
        setEditingReview(record);
        form.setFieldsValue(record);
        setModalVisible(true);
    };

    const handleDelete = async (id: string) => {
        try {
            setLoading(true);
            await apiClient.delete(`/reviews/${id}`);
            message.success('Review deleted successfully');
            fetchReviews();
        } catch (error) {
            message.error('Failed to delete review');
            console.error('Error deleting review:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleActive = async (id: string, currentStatus: boolean) => {
        try {
            await apiClient.put(`/reviews/${id}`, { is_active: !currentStatus });
            message.success(`Review ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
            fetchReviews();
        } catch (error) {
            message.error('Failed to update review status');
            console.error('Error updating review status:', error);
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            if (editingReview) {
                await apiClient.put(`/reviews/${editingReview.id}`, values);
                message.success('Review updated successfully');
            } else {
                await apiClient.post('/reviews', values);
                message.success('Review created successfully');
            }

            setModalVisible(false);
            form.resetFields();
            fetchReviews();
        } catch (error: any) {
            if (error.errorFields) {
                message.error('Please fill in all required fields');
            } else {
                message.error('Failed to save review');
                console.error('Error saving review:', error);
            }
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            width: '20%',
            ellipsis: true,
            render: (text: string) => (
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {text}
                </div>
            ),
        },
        {
            title: 'Rating',
            dataIndex: 'rate',
            key: 'rate',
            width: '12%',
            render: (rate: number) => (
                <div style={{ whiteSpace: 'nowrap' }}>
                    <Rate disabled value={rate} style={{ fontSize: 14 }} />
                </div>
            ),
        },
        {
            title: 'Review',
            dataIndex: 'review',
            key: 'review',
            width: '35%',
            ellipsis: true,
            render: (text: string) => (
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {text}
                </div>
            ),
        },
        {
            title: 'User Name',
            dataIndex: 'user_name',
            key: 'user_name',
            width: '10%',
        },
        {
            title: 'Source',
            dataIndex: 'platform',
            key: 'platform',
            width: '8%',
            align: 'center' as const,
            render: (platform: string | null) => {
                const icon = getPlatformIcon(platform);
                const found = PLATFORM_OPTIONS.find(p => p.value === platform);
                return icon ? (
                    <Tooltip title={found?.label}>
                        <span style={{ fontSize: 18 }}>{icon}</span>
                    </Tooltip>
                ) : <span style={{ color: '#d1d5db' }}>—</span>;
            },
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            width: '10%',
        },
        {
            title: 'Active',
            dataIndex: 'is_active',
            key: 'is_active',
            width: '7%',
            align: 'center' as const,
            render: (is_active: boolean, record: Review) => (
                <Switch
                    checked={is_active}
                    size="small"
                    onChange={() => handleToggleActive(record.id, is_active)}
                />
            ),
        },
        {
            title: 'Homepage',
            dataIndex: 'show_on_homepage',
            key: 'show_on_homepage',
            width: '7%',
            align: 'center' as const,
            render: (show_on_homepage: boolean, record: Review) => (
                <Switch
                    checked={show_on_homepage}
                    size="small"
                    onChange={async () => {
                        try {
                            await apiClient.put(`/reviews/${record.id}`, { show_on_homepage: !show_on_homepage });
                            message.success(`Review ${!show_on_homepage ? 'added to' : 'removed from'} homepage`);
                            fetchReviews();
                        } catch (error) {
                            message.error('Failed to update homepage status');
                        }
                    }}
                />
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: '10%',
            align: 'center' as const,
            render: (_: any, record: Review) => (
                <Space size="small">
                    <Tooltip title="Edit">
                        <Button
                            type="link"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                            size="small"
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Delete Review"
                        description="Are you sure you want to delete this review?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Tooltip title="Delete">
                            <Button
                                type="link"
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Container>
            <Card
                style={{ margin: '24px', borderRadius: '8px' }}
                bodyStyle={{ padding: '24px' }}
            >
                <div style={{
                    marginBottom: 24,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingBottom: 16,
                    borderBottom: '1px solid #f0f0f0'
                }}>
                    <Title level={3} style={{ margin: 0, fontWeight: 600 }}>Customer Reviews</Title>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAdd}
                        size="large"
                    >
                        Add Review
                    </Button>
                </div>

                <Table
                    columns={columns}
                    dataSource={reviews}
                    loading={loading}
                    rowKey="id"
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50'],
                        showTotal: (total) => `Total ${total} review${total !== 1 ? 's' : ''}`,
                    }}
                    scroll={{ x: 1200 }}
                    bordered
                />

                <Modal
                    title={<span style={{ fontSize: 18, fontWeight: 600 }}>{editingReview ? 'Edit Review' : 'Add New Review'}</span>}
                    open={modalVisible}
                    onOk={handleSubmit}
                    onCancel={() => {
                        setModalVisible(false);
                        form.resetFields();
                    }}
                    width={650}
                    confirmLoading={loading}
                    okText={editingReview ? 'Update' : 'Create'}
                    cancelText="Cancel"
                >
                    <Form
                        form={form}
                        layout="vertical"
                        style={{ marginTop: 16 }}
                    >
                        <Form.Item
                            name="title"
                            label="Review Title"
                            rules={[{ required: true, message: 'Please enter review title' }]}
                        >
                            <Input placeholder="e.g., Excellent Service" size="large" />
                        </Form.Item>

                        <Form.Item
                            name="rate"
                            label="Rating"
                            rules={[{ required: true, message: 'Please select rating' }]}
                        >
                            <Rate style={{ fontSize: 24 }} />
                        </Form.Item>

                        <Form.Item
                            name="review"
                            label="Review Text"
                            rules={[{ required: true, message: 'Please enter review text' }]}
                        >
                            <TextArea
                                rows={5}
                                placeholder="Enter the customer's review here..."
                                showCount
                                maxLength={500}
                            />
                        </Form.Item>

                        <Space style={{ width: '100%' }} size="large">
                            <Form.Item
                                name="user_name"
                                label="Customer Name"
                                rules={[{ required: true, message: 'Please enter customer name' }]}
                                style={{ flex: 1, marginBottom: 0 }}
                            >
                                <Input placeholder="e.g., Sarah M." size="large" />
                            </Form.Item>

                            <Form.Item
                                name="date"
                                label="Date"
                                rules={[{ required: true, message: 'Please enter date' }]}
                                style={{ flex: 1, marginBottom: 0 }}
                            >
                                <Input placeholder="e.g., 01/08/26" size="large" />
                            </Form.Item>
                        </Space>

                        <Form.Item
                            name="platform"
                            label="Review Source"
                            style={{ marginTop: 16 }}
                        >
                            <Select
                                placeholder="Select where the review came from"
                                allowClear
                                size="large"
                                options={PLATFORM_OPTIONS.map(p => ({
                                    value: p.value,
                                    label: <span>{p.icon} &nbsp;{p.label}</span>,
                                }))}
                            />
                        </Form.Item>

                        <Space style={{ width: '100%' }} size="large">
                            <Form.Item
                                name="is_active"
                                label="Display on Website"
                                valuePropName="checked"
                                style={{ marginBottom: 0 }}
                            >
                                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                            </Form.Item>

                            <Form.Item
                                name="show_on_homepage"
                                label="Show on Homepage"
                                valuePropName="checked"
                                style={{ marginBottom: 0 }}
                            >
                                <Switch checkedChildren="Yes" unCheckedChildren="No" />
                            </Form.Item>
                        </Space>
                    </Form>
                </Modal>
            </Card>
        </Container>
    );
};

export default Reviews;
